import type { TruckMotion } from "@townsy/world";

/** A buffered truck pose at a key: the server time (ms) or, for RemoteTrack, the truck's step count. */
export interface Snap {
  t: number;
  m: TruckMotion;
}

function lerpAngle(a: number, b: number, t: number): number {
  return a + Math.atan2(Math.sin(b - a), Math.cos(b - a)) * t;
}

/** A jump this long between two buffered poses is a teleport (a tow, the race grid): drawn at once, not slid over. */
export const TELEPORT_METRES = 8;

/** The pose at key `t` from buffered poses (holds the first / newest outside them; snaps over a teleport). */
export function sampleSnaps(snaps: readonly Snap[], t: number): TruckMotion | null {
  if (snaps.length === 0) return null;
  if (t <= snaps[0]!.t) return snaps[0]!.m;
  for (let i = 1; i < snaps.length; i++) {
    const b = snaps[i]!;
    if (b.t < t) continue;
    const a = snaps[i - 1]!;
    if (Math.hypot(b.m.x - a.m.x, b.m.z - a.m.z) > TELEPORT_METRES) return b.m;
    const f = b.t > a.t ? (t - a.t) / (b.t - a.t) : 1;
    return {
      x: a.m.x + (b.m.x - a.m.x) * f,
      z: a.m.z + (b.m.z - a.m.z) * f,
      y: a.m.y + (b.m.y - a.m.y) * f,
      vy: b.m.vy,
      heading: lerpAngle(a.m.heading, b.m.heading, f),
      speed: a.m.speed + (b.m.speed - a.m.speed) * f,
      airborne: b.m.airborne || a.m.airborne,
    };
  }
  return snaps[snaps.length - 1]!.m;
}

/** Steps behind the newest received step a remote truck is drawn (3 steps = 150 ms at 20 Hz). */
export const REMOTE_LAG_STEPS = 4;
/** Further than this from where it should be (a new room, a long stall): jump to it instead of catching up. */
const RESYNC_STEPS = 12;
/** Time constant (ms) of the gentle catch-up toward the target lag. */
const CATCH_UP_MS = 400;
const MAX_SNAPS = 16;
/** The head estimate runs at most this far past the newest received step (a truck that stopped stops being guessed). */
const MAX_HEAD_STEPS = 2;

/**
 * Another visitor's (or an AI's) truck, drawn from the shared states (ADR-0032). Each state carries the truck's step
 * count (`TruckView.steps`): one 50 ms simulation step per applied input or AI step. A driver's inputs reach the
 * server unevenly (two in one tick, none in the next), so the truck's pose against server time stutters, but against
 * its own step count it is perfectly even. The track plays the truck back at 20 steps a second a little behind the
 * newest step, catching up or easing off gently to keep that distance, never moving backward, and stopping at the
 * newest pose when the truck stops.
 */
export class RemoteTrack {
  private snaps: Snap[] = [];
  private cursor: number | null = null;
  /** Playback time (ms, the sum of frame lengths) and when the newest step arrived on it. */
  private clock = 0;
  private newestAt = 0;
  constructor(private readonly stepMs = 50) {}

  /** A state's pose of this truck at step `steps`. */
  push(steps: number, m: TruckMotion): void {
    if (!Number.isFinite(steps)) return;
    const last = this.snaps[this.snaps.length - 1];
    if (last && steps < last.t) {
      // The count went back (the room started over): start again from this pose.
      this.snaps = [];
      this.cursor = null;
    } else if (last && steps === last.t) {
      last.m = m;
      return;
    }
    this.snaps.push({ t: steps, m });
    this.newestAt = this.clock;
    if (this.snaps.length > MAX_SNAPS) this.snaps.splice(0, this.snaps.length - MAX_SNAPS);
  }

  /** Advance the playback by one frame (`dtMs`) and return the pose to draw (null before the first state). */
  advance(dtMs: number): TruckMotion | null {
    if (this.snaps.length === 0) return null;
    const dt = Math.max(0, Math.min(250, Number.isFinite(dtMs) ? dtMs : 0));
    this.clock += dt;
    const first = this.snaps[0]!.t;
    const newest = this.snaps[this.snaps.length - 1]!.t;
    // Where the truck is now, by the step clock: the newest step plus the time since it arrived (states come about
    // every 1.3 steps), so the target moves evenly instead of in a saw-tooth.
    const head = newest + Math.min(MAX_HEAD_STEPS, (this.clock - this.newestAt) / this.stepMs);
    const target = head - REMOTE_LAG_STEPS;
    if (this.cursor === null || Math.abs(target - this.cursor) > RESYNC_STEPS) {
      this.cursor = Math.max(first, target);
    } else {
      const step = dt / this.stepMs + (target - this.cursor) * (1 - Math.exp(-dt / CATCH_UP_MS));
      this.cursor += Math.max(0, step);
    }
    this.cursor = Math.min(newest, Math.max(first, this.cursor));
    return sampleSnaps(this.snaps, this.cursor);
  }

  /** Steps behind the newest received step (diagnostics and tests). */
  get lag(): number {
    const newest = this.snaps[this.snaps.length - 1]?.t ?? 0;
    return this.cursor === null ? 0 : newest - this.cursor;
  }
}
